export type StockFilterStored = {
    vendors: string[];
    showAll: boolean;
    categoryId: number | null;
};

const storageKey = (userId: number): string => `voltox-${userId}-warehouse-stock-filter`;
const showCreditStorageKey = (userId: number): string => `voltox-${userId}-warehouse-show-credit`;

const normalizeFilter = (value: unknown): StockFilterStored | null => {
    if (!value || typeof value !== 'object') return null;
    const row = value as Record<string, unknown>;
    const vendors = Array.isArray(row.vendors)
        ? row.vendors.map(String).filter(Boolean)
        : [];
    const showAll = Boolean(row.showAll);
    const categoryIdRaw = Number(row.categoryId);
    const categoryId = Number.isFinite(categoryIdRaw) && categoryIdRaw > 0 ? categoryIdRaw : null;
    if (!vendors.length && !showAll && !categoryId) return null;
    return { vendors, showAll, categoryId };
};

export const loadStockFilter = (userId: number): StockFilterStored | null => {
    if (!userId || typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(storageKey(userId));
        if (!raw) return null;
        return normalizeFilter(JSON.parse(raw));
    } catch {
        return null;
    }
};

export const saveStockFilter = (
    userId: number,
    filter: Record<string, unknown> | null | undefined,
): void => {
    if (!userId || typeof window === 'undefined') return;
    try {
        const normalized = normalizeFilter(filter);
        if (!normalized) {
            window.localStorage.removeItem(storageKey(userId));
            return;
        }
        window.localStorage.setItem(storageKey(userId), JSON.stringify(normalized));
    } catch {
        /* ignore quota / private mode */
    }
};

export const loadShowCredit = (userId: number): boolean => {
    if (!userId || typeof window === 'undefined') return false;
    try {
        const raw = window.localStorage.getItem(showCreditStorageKey(userId));
        if (raw === null) return false;
        return safeJSONParseBoolean(raw);
    } catch {
        return false;
    }
};

export const saveShowCredit = (userId: number, showCredit: boolean): void => {
    if (!userId || typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(showCreditStorageKey(userId), JSON.stringify(Boolean(showCredit)));
    } catch {
        /* ignore quota / private mode */
    }
};

const safeJSONParseBoolean = (raw: string): boolean => {
    try {
        return JSON.parse(raw) === true;
    } catch {
        return raw === '1' || raw === 'true';
    }
};
